import { useEffect, useState } from "react";
import api, { apiError, fmtUSD, fmtPct } from "@/lib/api";
import { Panel, Table, StatusBadge, Empty, Pct } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Zap } from "lucide-react";

export default function Deploy() {
  const [data, setData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [asset, setAsset] = useState("BTC");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejection, setRejection] = useState(null);

  const load = () => {
    api.get("/deploy/evaluate").then((r) => setData(r.data)).catch(() => {});
    api.get("/proposals").then((r) => setProposals(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const propose = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await api.post("/deploy/propose", { asset, amount: parseFloat(amount) });
      if (p.data.status === "REJECTED") {
        setRejection(p.data.violations || p.data.risk_result?.violations || []);
        toast.error(`Risk Governor rejected: ${(p.data.violations || [])[0]?.detail || "rule violation"}`);
      }
      else if (p.data.status === "RECONCILED") { setRejection(null); toast.success(`Executed & reconciled (PAPER): ${p.data.fill?.quantity} ${asset}`); }
      else { setRejection(null); toast.success(`Proposal ${p.data.status}`); }
      setAmount("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const act = async (pid, action) => {
    try {
      await api.post(`/proposals/${pid}/${action}`);
      toast.success(`Proposal ${action}d`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="deploy-page">
      <h2 className="font-display text-xl font-bold">Deploy Capital — Adaptive DCA</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.evaluations.map((ev) => (
          <Panel key={ev.asset} title={`${ev.asset} Deployment Analysis`} testId={`adaptive-dca-engine-panel-${ev.asset}`}>
            {ev.error ? <Empty>{ev.error}</Empty> : (
              <div className="space-y-3">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="metric-label">Deployment Score</div>
                    <div className={`font-mono text-4xl font-extrabold tabular-nums ${ev.deployment_score >= 60 ? "text-emerald-400" : ev.deployment_score >= 40 ? "gold-text" : "text-slate-400"}`}
                      data-testid={`deployment-score-${ev.asset}`}>{ev.deployment_score}</div>
                  </div>
                  <div className="text-right text-xs text-slate-500 space-y-0.5">
                    <div>Multiplier <span className="font-mono text-slate-200">{ev.multiplier}x</span></div>
                    <div>Suggested <span className="font-mono text-slate-200">{fmtUSD(ev.suggested_amount)}</span></div>
                  </div>
                </div>
                <div className="h-1.5 bg-[#121316] rounded-sm overflow-hidden">
                  <div className="h-full bg-[#D4AF37] transition-[width]" style={{ width: `${ev.deployment_score}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  <span>Price <span className="font-mono text-slate-300 float-right">{fmtUSD(ev.price)}</span></span>
                  <span>24h <Pct value={(ev.change_24h || 0) * 100} className="float-right" /></span>
                  <span>7d <Pct value={(ev.change_7d || 0) * 100} className="float-right" /></span>
                  <span>30d <Pct value={(ev.change_30d || 0) * 100} className="float-right" /></span>
                  <span>90d <Pct value={(ev.change_90d || 0) * 100} className="float-right" /></span>
                  <span>From 90d high <span className="font-mono text-slate-300 float-right">-{ev.distance_from_high_pct}%</span></span>
                  <span>Weight <span className="font-mono text-slate-300 float-right">{ev.current_weight_pct}% / {ev.target_weight_pct}%</span></span>
                  <span>Volatility (30d) <span className="font-mono text-slate-300 float-right">{(ev.volatility_30d * 100).toFixed(1)}%</span></span>
                  <span>News Risk <span className="font-mono text-slate-300 float-right" data-testid={`news-risk-${ev.asset}`}>{ev.intelligence?.news_risk == null ? "—" : `${ev.intelligence.news_risk}/100`}</span></span>
                  <span>News Opportunity <span className="font-mono text-slate-300 float-right">{ev.intelligence?.news_opportunity == null ? "—" : `${ev.intelligence.news_opportunity}/100`}</span></span>
                </div>
                {ev.recommendation && <div className="text-[11px] font-semibold text-slate-300" data-testid={`recommendation-${ev.asset}`}>Treasury recommendation: <span className="gold-text">{ev.recommendation}</span></div>}
                {ev.intel_note && <div className="text-[10px] text-amber-300/90 border-l-2 border-amber-600 pl-2" data-testid={`intel-note-${ev.asset}`}>{ev.intel_note}</div>}
                <div className="text-[10px] text-slate-600">Source: {ev.price_source}{ev.price_stale ? " (stale)" : ""}</div>
              </div>
            )}
          </Panel>
        ))}
        <Panel title="Manual Proposal" testId="deploy-propose-panel">
          <form onSubmit={propose} className="space-y-3">
            <div className="metric-label">Asset</div>
            <Select value={asset} onValueChange={setAsset}>
              <SelectTrigger data-testid="deploy-asset-select" className="bg-[#121316] border-[#2D323E]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#17191E] border-[#2D323E]">
                {data.evaluations.map((ev) => <SelectItem key={ev.asset} value={ev.asset} data-testid={`deploy-asset-${ev.asset}`}>{ev.asset}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="metric-label">Amount (USD)</div>
            <Input data-testid="deploy-amount-input" type="number" min="1" step="0.01" required value={amount}
              onChange={(e) => setAmount(e.target.value)} className="bg-[#121316] border-[#2D323E] font-mono" placeholder="8.00" />
            <Button data-testid="deploy-propose-button" type="submit" disabled={busy}
              className="w-full bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
              <Zap className="w-4 h-4 mr-2" />{busy ? "PROCESSING…" : "PROPOSE & EVALUATE"}
            </Button>
            {rejection && (
              <div className="border border-red-800/60 bg-red-950/30 rounded-sm px-3 py-2 space-y-1" data-testid="deploy-rejection-box">
                <div className="text-red-400 text-xs font-bold">RISK GOVERNOR: REJECTED</div>
                {rejection.map((v, i) => <div key={i} className="text-red-300 text-[11px]">✕ {v.rule}: {v.detail}</div>)}
              </div>
            )}
            <p className="text-[10px] text-slate-600">Every proposal passes: Treasury Decision → Risk Governor → Execution. In PAPER mode approved proposals fill immediately at real market prices.</p>
          </form>
        </Panel>
      </div>

      <Panel title="Trade Proposals" testId="proposals-panel">
        {proposals.length === 0 ? <Empty>No proposals yet.</Empty> : (
          <Table testId="proposals-table" head={[{ label: "Created" }, { label: "Asset" }, { label: "Amount", num: true }, { label: "Score", num: true }, { label: "Mode" }, { label: "Status" }, { label: "Actions" }]}>
            {proposals.map((p) => (
              <tr key={p.id} className="hover:bg-[#222630]" data-testid={`proposal-row-${p.id.slice(0, 8)}`}>
                <td className="td-cell font-mono">{new Date(p.created_at).toLocaleString()}</td>
                <td className="td-cell font-semibold">{p.asset}</td>
                <td className="td-num">{fmtUSD(p.amount)}</td>
                <td className="td-num">{p.deployment_score != null ? Number(p.deployment_score).toFixed(1) : "—"}</td>
                <td className="td-cell"><StatusBadge status={p.mode} /></td>
                <td className="td-cell"><StatusBadge status={p.status} testId={`proposal-status-${p.id.slice(0, 8)}`} /></td>
                <td className="td-cell">
                  {p.status === "APPROVED" && (
                    <div className="flex gap-2">
                      {!p.human_approved && <Button size="sm" variant="outline" data-testid={`approve-proposal-btn-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "approve")} className="h-7 text-[11px] border-emerald-800 text-emerald-400">Approve</Button>}
                      <Button size="sm" variant="outline" data-testid={`execute-proposal-btn-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "execute")} className="h-7 text-[11px] border-[#D4AF37]/50 text-[#D4AF37]">Execute</Button>
                      <Button size="sm" variant="outline" data-testid={`reject-proposal-btn-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "reject")} className="h-7 text-[11px] border-red-800 text-red-400">Cancel</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
